import {gql} from '@apollo/client';
import React from 'react';

import {NucleotideType} from 'biojs/Util';
import {BulkSetModsParams} from 'constants/SequenceOpConstants';
import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  BiointelligentLinkContainerCountQuery,
  BiointelligentLinkContainerCountQueryVariables,
} from 'molbio/biointelligent-links/__generated/BiointelligentLinkContainerCountQuery';
import {BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY} from 'molbio/biointelligent-links/BiointelligentLinkQueries';
import {Header1} from 'ui-library/Headers';

import {
  ModifiedNucleotidesQuery,
  ModifiedNucleotidesQueryVariables,
} from './__generated/ModifiedNucleotidesQuery';
import {MonomerQuery} from './__generated/MonomerQuery';
import {MONOMER_QUERY, NucleotideComponent} from './MonomerConstants';
import NucleotidesTable from './NucleotidesTable';

export const MODIFIED_NUCLEOTIDES_QUERY = gql`
  query ModifiedNucleotidesQuery($sequenceId: StrictString!) {
    componentSequence(id: $sequenceId) @client {
      id @client
      nucleotideType @client
      type @client
      modifiedNucleotides @client {
        position @client
        sugarMonomerId @client
        phosphateMonomerId @client
        baseMonomerId @client
      }
    }
  }
`;

interface Props {
  areEditsEnabled: boolean;
  bases: string;
  sequenceId: string;
  nucleotideType: NucleotideType;
  onUpdateMod: (position: number, monomerId: string | null, monomerType: NucleotideComponent) => void;
  onBulkUpdateMods: (params: BulkSetModsParams) => void;
}

export default function NucleotidesTab({
  areEditsEnabled,
  bases,
  sequenceId,
  nucleotideType,
  onUpdateMod,
  onBulkUpdateMods,
}: Props): JSX.Element | null {
  return (
    <>
      <Header1>Nucleotides</Header1>
      <BenchlingQuery<MonomerQuery>
        query={MONOMER_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      >
        {({monomers}) => (
          <BenchlingQuery<
            BiointelligentLinkContainerCountQuery,
            BiointelligentLinkContainerCountQueryVariables
          >
            query={BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY}
            renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
            variables={{id: sequenceId}}
          >
            {(bioentityData) => (
              <BenchlingQuery<ModifiedNucleotidesQuery, ModifiedNucleotidesQueryVariables>
                query={MODIFIED_NUCLEOTIDES_QUERY}
                renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
                variables={{sequenceId}}
                fetchPolicy="cache-only"
              >
                {({componentSequence}) => (
                  <NucleotidesTable
                    areNucleotideEditsEnabled={
                      areEditsEnabled &&
                      !isInaccessible(bioentityData.polymer) &&
                      bioentityData.polymer.partContainerCount === 0
                    }
                    bases={bases}
                    modifiedNucleotides={componentSequence ? componentSequence.modifiedNucleotides : []}
                    monomers={monomers}
                    nucleotideType={nucleotideType}
                    onUpdateMod={onUpdateMod}
                    onBulkUpdateMods={onBulkUpdateMods}
                    sequenceId={sequenceId}
                    isOligo={componentSequence?.type === SequenceType.oligo}
                  />
                )}
              </BenchlingQuery>
            )}
          </BenchlingQuery>
        )}
      </BenchlingQuery>
    </>
  );
}
